import { Edge, Node } from 'react-flow-renderer';

export interface TypeSuggestItem {
  /** the title of the attribute type (used for display) */
  title: string;
  /**
   * based on the selected type, the kind can be
   * - related entity
   * - related entities
   * - alias
   * - storage
   */
  kind: catalog.AttributeKind;
  /**
   * the calculated kind holds the final value of the attribute kind property.
   * it is resolved based on the selected type.
   */
  calculatedKind: catalog.AttributeKind;
  /** the actual value of the type.
   * - can be a primitive type (date, number, bool, ...etc)
   * - can be a dataclass name or a collection name ( related attribute )
   * - can be a full path to an existing attribute. (alias)
   */
  value: string;
  /**
   * it holds to final value for the attributes type property. it is resolved based on the selected type.
   */
  calculatedType: string;
  /**
   * optional property defining the attribute behavior
   */
  behavior?: string;
}
export type TypeSuggestItems = TypeSuggestItem[];

// -----------React Flow Custom Element Types--------------------

export type ModelNode = Omit<Node, 'type' | 'data'> & {
  data: catalog.IDataClass;
  type: 'table';
  virtual?: boolean;
};

export type ModelEdge = Omit<Edge, 'data'> & {
  data: {
    sourceDataclass: string;
    sourceAttribute?: string;
    targetDataclass: string;
    targetAttribute?: string;
  };
};

// ----------End Of React Flow Custom Element Types--------------

export interface ServerModel {
  toJSON: boolean;
  extraProperties?: catalog.IModelProperties;
  dataClasses: catalog.IDataClass[];
}

export type DataclassScope = 'public' | 'publicOnServer';
export type AttributeScope =
  | 'public'
  | 'publicOnServer'
  | 'protected'
  | 'private';
export type TIndexType = 'btree' | 'cluster' | 'auto' | undefined;
export enum EAttributeTypes {
  UUID = 'uuid',
  STRING = 'string',
  TEXT = 'text',
  DATE = 'date',
  TIME = 'duration',
  BOOLEAN = 'bool',
  BYTE = 'byte',
  INTEGER = 'word',
  LONG = 'long',
  LONG64 = 'long64',
  FLOAT = 'number',
  BLOB = 'blob',
  IMAGE = 'image',
  OBJECT = 'object',
}

export enum EAttributeKind {
  STORAGE = 'storage',
  RELATEDENTITY = 'relatedEntity',
  RELATEDENTITIES = 'relatedEntities',
  CALCULATED = 'calculated',
  COMPOSITION = 'composition',
  ALIAS = 'alias',
}
export const isValidPrimaryKey = (attribute: catalog.IAttribute) => {
  return ['string', 'uuid', 'number', 'long'].includes(attribute.type);
};

export const attributeKinds = [
  { label: 'Storage', value: 'storage' },
  { label: 'Calculated', value: 'calculated' },
];

export const primaryKeyAttributes: TypeSuggestItems = [
  {
    title: 'UUID',
    kind: EAttributeKind.STORAGE,
    value: 'uuid',
    calculatedType: 'uuid',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Long',
    kind: EAttributeKind.STORAGE,
    value: 'long',
    calculatedType: 'long',
    calculatedKind: EAttributeKind.STORAGE,
  },
];

export const attributeTypes: TypeSuggestItems = [
  {
    title: 'String',
    kind: EAttributeKind.STORAGE,
    value: 'string',
    calculatedType: 'string',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Text',
    kind: EAttributeKind.STORAGE,
    value: 'text',
    calculatedType: 'text',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'UUID',
    kind: EAttributeKind.STORAGE,
    value: 'uuid',
    calculatedType: 'uuid',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Date',
    kind: EAttributeKind.STORAGE,
    value: 'date',
    calculatedType: 'date',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Duration',
    kind: EAttributeKind.STORAGE,
    value: 'duration',
    calculatedType: 'duration',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Bool',
    kind: EAttributeKind.STORAGE,
    value: 'bool',
    calculatedType: 'bool',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Byte',
    kind: EAttributeKind.STORAGE,
    value: 'byte',
    calculatedType: 'byte',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Word',
    kind: EAttributeKind.STORAGE,
    value: 'word',
    calculatedType: 'word',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Number',
    kind: EAttributeKind.STORAGE,
    value: 'number',
    calculatedType: 'number',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Long',
    kind: EAttributeKind.STORAGE,
    value: 'long',
    calculatedType: 'long',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Object',
    kind: EAttributeKind.STORAGE,
    value: 'object',
    calculatedType: 'object',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Image',
    kind: EAttributeKind.STORAGE,
    value: 'image',
    calculatedType: 'image',
    calculatedKind: EAttributeKind.STORAGE,
  },
  {
    title: 'Blob',
    kind: EAttributeKind.STORAGE,
    value: 'blob',
    calculatedType: 'blob',
    calculatedKind: EAttributeKind.STORAGE,
  },
];

export const dataclassScopes = [
  { label: 'Expose as REST', value: 'public' },
  {
    label: 'Do not expose as REST',
    value: 'publicOnServer',
  },
];

export const attributeScopes = [
  { label: 'Expose as REST', value: 'public' },
  { label: 'Do not expose as REST', value: 'publicOnServer' },
];

export const indexTypes = [
  { label: 'None', value: 'none' },
  { label: 'B-tree', value: 'btree' },
  { label: 'Cluster', value: 'cluster' },
  { label: 'Automatic', value: 'auto' },
];
